import { useState } from 'react';
import { ArrowLeft, Camera, CheckCircle2, AlertTriangle, Shield, Users, Phone, MessageCircle } from 'lucide-react';

interface GearComplianceScreenProps {
  onBack: () => void;
  onComplete: () => void;
}

export function GearComplianceScreen({ onBack, onComplete }: GearComplianceScreenProps) {
  const [gearChecks, setGearChecks] = useState({
    vest: false,
    helmet: false,
    photo: false
  });

  const [gearPhotos, setGearPhotos] = useState({
    vest: null as string | null,
    helmet: null as string | null,
    full: null as string | null
  });

  const [acknowledgedPolicies, setAcknowledgedPolicies] = useState({
    noPersonalInfo: false,
    inAppOnly: false,
    gearRequired: false
  });

  const handlePhotoUpload = (type: 'vest' | 'helmet' | 'full') => {
    // Simulate photo upload
    alert(`📸 Camera would open to verify your ${type === 'vest' ? 'GO7 vest' : type === 'helmet' ? 'helmet' : 'full gear'}`);
    
    // Mock photo
    const mockPhoto = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&h=300&fit=crop';
    setGearPhotos(prev => ({ ...prev, [type]: mockPhoto }));
    
    if (type === 'vest') setGearChecks(prev => ({ ...prev, vest: true }));
    if (type === 'helmet') setGearChecks(prev => ({ ...prev, helmet: true }));
    if (type === 'full') setGearChecks(prev => ({ ...prev, photo: true }));
  };

  const allChecksComplete = Object.values(gearChecks).every(Boolean) && 
                            Object.values(acknowledgedPolicies).every(Boolean);

  return (
    <div className="fixed inset-0 bg-white flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#E0001B] to-[#FF1A35] px-4 py-4">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <ArrowLeft className="w-6 h-6 text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-[20px] text-white font-semibold">Safety & Compliance Check</h1>
            <p className="text-[12px] text-white/80">Required before going online</p>
          </div>
          <Shield className="w-8 h-8 text-white/80" />
        </div>

        {/* Progress */}
        <div className="bg-white/20 rounded-full h-2 overflow-hidden">
          <div 
            className="bg-white h-full transition-all duration-500"
            style={{ width: `${(Object.values({...gearChecks, ...acknowledgedPolicies}).filter(Boolean).length / 6) * 100}%` }}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        {/* CRITICAL POLICY WARNING */}
        <div className="bg-red-50 border-2 border-red-500 rounded-[16px] p-4 mb-6">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-[16px] font-semibold text-red-900 mb-1">⚠️ CRITICAL: Communication Policy</h3>
              <p className="text-[13px] text-red-800 leading-relaxed">
                <b>STRICTLY PROHIBITED:</b> Sharing personal contact information with customers or using any communication outside the GO7 app.
              </p>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-3 space-y-2">
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-[12px] font-bold">✕</span>
              </div>
              <p className="text-[12px] text-gray-900 flex-1">
                <b>NO</b> sharing phone numbers, WhatsApp, social media, or personal email
              </p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-[12px] font-bold">✕</span>
              </div>
              <p className="text-[12px] text-gray-900 flex-1">
                <b>NO</b> arranging deliveries or communication outside the app
              </p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-[12px] font-bold">✕</span>
              </div>
              <p className="text-[12px] text-gray-900 flex-1">
                <b>NO</b> asking customers to contact you directly for future deliveries
              </p>
            </div>
          </div>

          <div className="mt-3 bg-red-900 text-white rounded-lg p-3">
            <p className="text-[13px] font-semibold mb-1">💰 PENALTIES FOR VIOLATIONS:</p>
            <ul className="text-[12px] space-y-1 ml-4 list-disc">
              <li><b>1st Offense:</b> ₦10,000 fine + warning</li>
              <li><b>2nd Offense:</b> ₦25,000 fine + 7-day suspension</li>
              <li><b>3rd Offense:</b> Account termination + legal action</li>
            </ul>
          </div>
        </div>

        {/* Communication Policy Acknowledgment */}
        <div className="mb-6">
          <h3 className="text-[16px] font-semibold mb-3 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#E0001B]" />
            Policy Acknowledgment
          </h3>
          
          <div className="space-y-3">
            <label className={`flex items-start gap-3 p-4 border-2 rounded-[12px] cursor-pointer transition-all ${
              acknowledgedPolicies.noPersonalInfo 
                ? 'border-[#00B277] bg-[#00B277]/5' 
                : 'border-gray-200 hover:border-gray-300'
            }`}>
              <input
                type="checkbox"
                checked={acknowledgedPolicies.noPersonalInfo}
                onChange={(e) => setAcknowledgedPolicies(prev => ({ ...prev, noPersonalInfo: e.target.checked }))}
                className="w-5 h-5 text-[#00B277] rounded border-gray-300 focus:ring-[#00B277] mt-0.5"
              />
              <div className="flex-1">
                <p className="text-[14px] font-medium text-[#111111] mb-1">
                  No Personal Information Sharing
                </p>
                <p className="text-[12px] text-[#6B6B6B]">
                  I will NOT share my phone number, WhatsApp, email, or any personal contact details with customers
                </p>
              </div>
            </label>

            <label className={`flex items-start gap-3 p-4 border-2 rounded-[12px] cursor-pointer transition-all ${
              acknowledgedPolicies.inAppOnly 
                ? 'border-[#00B277] bg-[#00B277]/5' 
                : 'border-gray-200 hover:border-gray-300'
            }`}>
              <input
                type="checkbox"
                checked={acknowledgedPolicies.inAppOnly}
                onChange={(e) => setAcknowledgedPolicies(prev => ({ ...prev, inAppOnly: e.target.checked }))}
                className="w-5 h-5 text-[#00B277] rounded border-gray-300 focus:ring-[#00B277] mt-0.5"
              />
              <div className="flex-1">
                <p className="text-[14px] font-medium text-[#111111] mb-1">
                  In-App Communication ONLY
                </p>
                <p className="text-[12px] text-[#6B6B6B]">
                  I will ONLY communicate with customers through the GO7 app chat and calling features
                </p>
              </div>
            </label>

            <label className={`flex items-start gap-3 p-4 border-2 rounded-[12px] cursor-pointer transition-all ${
              acknowledgedPolicies.gearRequired 
                ? 'border-[#00B277] bg-[#00B277]/5' 
                : 'border-gray-200 hover:border-gray-300'
            }`}>
              <input
                type="checkbox"
                checked={acknowledgedPolicies.gearRequired}
                onChange={(e) => setAcknowledgedPolicies(prev => ({ ...prev, gearRequired: e.target.checked }))}
                className="w-5 h-5 text-[#00B277] rounded border-gray-300 focus:ring-[#00B277] mt-0.5"
              />
              <div className="flex-1">
                <p className="text-[14px] font-medium text-[#111111] mb-1">
                  Mandatory Safety Gear
                </p>
                <p className="text-[12px] text-[#6B6B6B]">
                  I understand I MUST wear GO7 vest, helmet, and proper gear at ALL times while on duty
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Gear Verification */}
        <div className="mb-6">
          <h3 className="text-[16px] font-semibold mb-3 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#E0001B]" />
            Safety Gear Verification
          </h3>

          <div className="space-y-3">
            {/* GO7 Vest */}
            <div className={`border-2 rounded-[12px] p-4 transition-all ${
              gearChecks.vest ? 'border-[#00B277] bg-[#00B277]/5' : 'border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  {gearChecks.vest ? (
                    <CheckCircle2 className="w-6 h-6 text-[#00B277]" />
                  ) : (
                    <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                  )}
                  <div>
                    <p className="text-[14px] font-medium text-[#111111]">GO7 Safety Vest</p>
                    <p className="text-[11px] text-[#6B6B6B]">Official branded vest required</p>
                  </div>
                </div>
                <button
                  onClick={() => handlePhotoUpload('vest')}
                  className="px-4 py-2 rounded-[8px] bg-[#E0001B] text-white hover:bg-[#C00017] transition-colors text-[12px] flex items-center gap-2"
                >
                  <Camera className="w-4 h-4" />
                  {gearPhotos.vest ? 'Retake' : 'Verify'}
                </button>
              </div>
              {gearPhotos.vest && (
                <img src={gearPhotos.vest} alt="GO7 Vest" className="w-full h-32 object-cover rounded-lg" />
              )}
            </div>

            {/* Helmet */}
            <div className={`border-2 rounded-[12px] p-4 transition-all ${
              gearChecks.helmet ? 'border-[#00B277] bg-[#00B277]/5' : 'border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  {gearChecks.helmet ? (
                    <CheckCircle2 className="w-6 h-6 text-[#00B277]" />
                  ) : (
                    <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                  )}
                  <div>
                    <p className="text-[14px] font-medium text-[#111111]">Safety Helmet</p>
                    <p className="text-[11px] text-[#6B6B6B]">DOT/ECE certified helmet</p>
                  </div>
                </div>
                <button
                  onClick={() => handlePhotoUpload('helmet')}
                  className="px-4 py-2 rounded-[8px] bg-[#E0001B] text-white hover:bg-[#C00017] transition-colors text-[12px] flex items-center gap-2"
                >
                  <Camera className="w-4 h-4" />
                  {gearPhotos.helmet ? 'Retake' : 'Verify'}
                </button>
              </div>
              {gearPhotos.helmet && (
                <img src={gearPhotos.helmet} alt="Helmet" className="w-full h-32 object-cover rounded-lg" />
              )}
            </div>

            {/* Full Gear Photo */}
            <div className={`border-2 rounded-[12px] p-4 transition-all ${
              gearChecks.photo ? 'border-[#00B277] bg-[#00B277]/5' : 'border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  {gearChecks.photo ? (
                    <CheckCircle2 className="w-6 h-6 text-[#00B277]" />
                  ) : (
                    <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                  )}
                  <div>
                    <p className="text-[14px] font-medium text-[#111111]">Full Gear Photo</p>
                    <p className="text-[11px] text-[#6B6B6B]">Selfie with vest & helmet on</p>
                  </div>
                </div>
                <button
                  onClick={() => handlePhotoUpload('full')}
                  className="px-4 py-2 rounded-[8px] bg-[#E0001B] text-white hover:bg-[#C00017] transition-colors text-[12px] flex items-center gap-2"
                >
                  <Camera className="w-4 h-4" />
                  {gearPhotos.full ? 'Retake' : 'Take Photo'}
                </button>
              </div>
              {gearPhotos.full && (
                <img src={gearPhotos.full} alt="Full Gear" className="w-full h-32 object-cover rounded-lg" />
              )}
            </div>
          </div>
        </div>

        {/* Warning Box */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-[12px] p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <div>
              <p className="text-[13px] text-yellow-900 font-medium mb-1">Random Compliance Checks</p>
              <p className="text-[12px] text-yellow-800">
                GO7 conducts random photo verification during shifts. Riders without proper gear will be suspended immediately.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 p-4">
        <button
          onClick={() => {
            if (allChecksComplete) {
              alert('✅ Compliance verified! You can now go online.');
              onComplete();
            }
          }}
          disabled={!allChecksComplete}
          className={`w-full h-14 rounded-[12px] font-semibold text-[16px] transition-all ${
            allChecksComplete
              ? 'bg-[#00B277] text-white hover:bg-[#009963] shadow-lg'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {allChecksComplete ? '✓ Complete Compliance Check' : `Complete All Items (${Object.values({...gearChecks, ...acknowledgedPolicies}).filter(Boolean).length}/6)`}
        </button>
        
        {!allChecksComplete && (
          <p className="text-[12px] text-center text-[#6B6B6B] mt-2">
            Complete all verifications to go online
          </p>
        )}
      </div>
    </div>
  );
}
